import {
  Base,
  MagnifyingGlassIcon,
  ReverLoading,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@itsrever/design-system'
import { clsx } from 'clsx'
import { ReactNode, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { PageHeader } from '@/ui/shared'
import {
  ApiKeyRow,
  Viewer,
  accessLabel,
  formatCreated,
  lastUsedLabel
} from './types'

const { Button, Card, Input } = Base

// The group is one per account today, but it is what the key actually grants on, so the row says
// it rather than leaving the reader to infer it from the banner.
const ROW_LAYOUT =
  'grid grid-cols-[1.2fr_0.8fr_1.8fr_1.4fr_1fr_136px] items-center gap-4 px-4'

interface ApiKeysSettingsProps {
  viewer: Viewer
  // The group whose keys are listed. Empty when REVER staff have no ecommerce selected.
  group: string
  keys: ApiKeyRow[]
  loading: boolean
  error: boolean
  onRetry: () => void
  onCreate: () => void
  onView: (key: ApiKeyRow) => void
  onRevoke: (key: ApiKeyRow) => void
}

export const ApiKeysSettings: React.FC<ApiKeysSettingsProps> = ({
  viewer,
  group,
  keys,
  loading,
  error,
  onRetry,
  onCreate,
  onView,
  onRevoke
}) => {
  const { t, i18n } = useTranslation()
  const [search, setSearch] = useState('')
  const isRever = viewer === 'rever'

  const query = search.trim().toLowerCase()
  const visibleKeys = query
    ? keys.filter((key) => key.name.toLowerCase().includes(query))
    : keys

  if (!group) {
    return (
      <>
        <PageHeader
          title={t('api_keys.title')}
          description={t('api_keys.description')}
        />
        <div className="flex max-w-[1000px] flex-col gap-4 px-7 py-4">
          <div className="rounded border border-solid border-neutral-200 bg-blue-50 p-4">
            <p className="text-sm leading-relaxed text-neutral-700">
              {t('api_keys.pick_ecommerce')}
            </p>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title={t('api_keys.title')}
        description={t('api_keys.description')}
        actions={
          <Button type="button" variant="primary" onClick={onCreate}>
            {t('api_keys.create')}
          </Button>
        }
      />
      <div className="flex max-w-[1000px] flex-col gap-4 px-7 py-4">
        <div className="rounded border border-solid border-neutral-200 bg-blue-50 p-4">
          <p className="text-sm leading-relaxed text-neutral-700">
            <Trans
              i18nKey={
                isRever ? 'api_keys.banner_staff' : 'api_keys.banner_merchant'
              }
              values={{ group }}
              components={{ b: <strong /> }}
            />
          </p>
        </div>

        {keys.length > 0 ? (
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            type="search"
            placeholder={t('api_keys.search_placeholder')}
            aria-label={t('api_keys.search_label')}
            prefix={<MagnifyingGlassIcon className="size-5" />}
            clear={search.length > 0}
            onClear={() => setSearch('')}
          />
        ) : null}

        {/* An error must not render as an empty list: "no keys yet" on a credentials page would
            invite issuing a duplicate of one that was never loaded. */}
        {error ? (
          <Card
            variant="flat"
            className="flex flex-col items-center gap-3 py-10"
          >
            <p className="text-sm text-neutral-600">
              {t('api_keys.load_error')}
            </p>
            <Button type="button" variant="secondary" onClick={onRetry}>
              {t('api_keys.retry')}
            </Button>
          </Card>
        ) : loading ? (
          <ReverLoading />
        ) : (
          <Card variant="flat">
            <div
              className={clsx(
                ROW_LAYOUT,
                'border-b border-neutral-200 py-2 text-xs text-grey-1'
              )}
            >
              <p className="text-xs">{t('api_keys.column_name')}</p>
              <p className="text-xs">{t('api_keys.column_group')}</p>
              <p className="text-xs">{t('api_keys.column_access')}</p>
              <p className="text-xs">{t('api_keys.column_created')}</p>
              <p className="text-xs">{t('api_keys.column_last_used')}</p>
              <p className="sr-only">{t('api_keys.column_actions')}</p>
            </div>

            {visibleKeys.length === 0 ? (
              <p className="px-4 py-6 text-sm text-neutral-600">
                {query ? t('api_keys.no_matches') : t('api_keys.empty')}
              </p>
            ) : null}

            {visibleKeys.map((key) => (
              <div
                key={`${key.group}/${key.name}`}
                className={clsx(
                  ROW_LAYOUT,
                  'border-b border-neutral-100 py-3 last:border-b-0'
                )}
              >
                <p className="truncate text-sm">{key.name}</p>
                <p className="truncate text-sm text-neutral-600">{key.group}</p>
                <p className="text-sm text-neutral-600">
                  {accessLabel(key.scopes, t)}
                </p>
                {/* An email is long and useless half-shown, so it wraps on its own line. */}
                <div className="min-w-0 text-neutral-600">
                  <p className="truncate text-sm">
                    {formatCreated(key.createdAt, i18n.language)}
                  </p>
                  {key.createdBy ? (
                    <p className="break-all text-xs">{key.createdBy}</p>
                  ) : null}
                </div>
                <p className="truncate text-sm text-neutral-600">
                  {lastUsedLabel(key.lastUsedAt, t, i18n.language)}
                </p>
                <div className="flex justify-end gap-2">
                  {isRever ? (
                    <DisabledWithReason
                      reason={t('api_keys.staff_cannot_read')}
                    >
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled
                      >
                        {t('api_keys.view')}
                      </Button>
                    </DisabledWithReason>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => onView(key)}
                    >
                      {t('api_keys.view')}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="border-red-200 text-red-500 data-[loading=false]:hover:bg-red-50"
                    onClick={() => onRevoke(key)}
                  >
                    {t('api_keys.delete')}
                  </Button>
                </div>
              </div>
            ))}
          </Card>
        )}
      </div>
    </>
  )
}

// A disabled button says nothing about why. The tooltip wraps a span because a disabled button
// fires no pointer events of its own.
const DisabledWithReason: React.FC<{
  reason: string
  children: ReactNode
}> = ({ reason, children }) => (
  <Tooltip placement="bottom">
    <TooltipTrigger asChild>
      <span className="inline-flex">{children}</span>
    </TooltipTrigger>
    <TooltipContent>
      <p className="max-w-56 text-xs">{reason}</p>
    </TooltipContent>
  </Tooltip>
)
